import { z } from 'zod';
import { isoDateSchema } from './planning.ts';

/** `POST /api/plan/propose` (P4.03): what the auto-planner should plan. */
export const weekPlanRequestSchema = z.object({
  /** The days to fill (in one week, in order). */
  days: z.array(isoDateSchema).min(1).max(7),
  /** Meal slot keys to fill each day ("lunch", "dinner"). */
  slots: z.array(z.string().min(1).max(40)).min(1).max(8),
  /** How many cook sessions at most. */
  sessions: z.number().int().min(1).max(10),
  /** Target per person and day; null = no target. */
  kcalPerDay: z.number().int().min(800).max(6000).nullable().default(null),
  /** Target for the whole plan in €; null = no target. */
  budgetEur: z.number().min(1).max(2000).nullable().default(null),
  /** Prefer cooking in batches that keep for several days. */
  mealPrep: z.boolean().default(true),
  /** Free text for the model ("vegetarisch", "keine Pilze", "Freitag essen wir auswärts"). */
  notes: z.string().trim().max(500).nullable().default(null),
});
export type WeekPlanRequest = z.input<typeof weekPlanRequestSchema>;

export const proposedSessionSchema = z.object({
  /** Links entries to their session inside the proposal. */
  key: z.string(),
  recipeId: z.uuid(),
  title: z.string(),
  date: isoDateSchema,
  portions: z.number().int().min(1),
  /** Why the model picked it ("nutzt den restlichen Lauch"). */
  reason: z.string().nullable(),
});
export type ProposedSession = z.infer<typeof proposedSessionSchema>;

export const proposedEntrySchema = z.object({
  date: isoDateSchema,
  slotKey: z.string(),
  sessionKey: z.string(),
  /** Portions for everyone at that meal (one per person, times nothing else). */
  portions: z.number().positive(),
});
export type ProposedEntry = z.infer<typeof proposedEntrySchema>;

/** A draft week: nothing is saved until the user applies it. */
export const weekPlanProposalSchema = z.object({
  sessions: z.array(proposedSessionSchema),
  entries: z.array(proposedEntrySchema),
  /** Average kcal per person over the planned days (counted meals only); null = unknown. */
  kcalPerDay: z.number().nullable(),
  /** Cost of all sessions; null = unknown. */
  costEur: z.number().nullable(),
  notes: z.string().nullable(),
});
export type WeekPlanProposal = z.infer<typeof weekPlanProposalSchema>;
